import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { usePermissions } from './usePermissions'

export interface TermCondition {
  id: string
  organizationId: string
  title: string
  content: string
  category: string
  isDefault: boolean
  displayOrder: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface TermConditionFilters {
  category?: string
  isActive?: boolean
  search?: string
}

export function useTermConditions(filters?: TermConditionFilters) {
  const { organization } = useAuth()
  const { can } = usePermissions()
  return useQuery({
    queryKey: ['termConditions', organization?.id, filters],
    queryFn: async () => {
      const params = new URLSearchParams({ organizationId: organization?.id || '' })
      if (filters?.category) params.set('category', filters.category)
      if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive))
      if (filters?.search) params.set('search', filters.search)
      const res = await api.get<any>(`/master-data/terms?${params}`)
      return (res?.data || []) as TermCondition[]
    },
    enabled: !!organization?.id && can('QUOTATIONS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useTermConditionMutations() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['termConditions', organization?.id] })
  }

  const create = useMutation({
    mutationFn: (payload: Omit<TermCondition, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) =>
      api.post<any>('/master-data/terms', { ...payload, organizationId: organization?.id }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<TermCondition> }) =>
      api.patch<any>(`/master-data/terms/${id}`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const setStatus = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch<any>(`/master-data/terms/${id}/status`, { isActive }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<any>(`/master-data/terms/${id}`, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const setDefault = useMutation({
    mutationFn: (id: string) =>
      api.post<any>(`/master-data/terms/${id}/set-default`, {}, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  return { create, update, setStatus, remove, setDefault }
}